import type { PermissionCatalog } from "./types";

// Human-readable labels for permission domains. Domains not listed here fall
// back to a title-cased version of the raw domain string so a newly added
// backend domain still renders something sensible before this map is updated.
const DOMAIN_LABELS: Record<string, string> = {
  allowlist: "Allowlist",
  destinationconfig: "Destination Configuration",
  eventlibrary: "Event Library",
  phi: "PHI",
  forms: "Forms",
  auditlog: "Audit Log",
  wtm: "Web Tracker Monitoring",
  analytics: "Analytics",
  offlineattributions: "Offline Attributions",
  product: "Product",
  video: "Video",
  audiences: "Audiences",
  consentmanagement: "Consent Management",
  activitylog: "Activity Log",
  userprofiles: "User Profiles",
  freshpaintai: "Freshpaint AI",
  heatmapssurveysandsessionreplay: "Heatmaps, Surveys & Session Replay",
};

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  read: "Read",
  write: "Write",
};

export function domainLabel(domain: string): string {
  return (
    DOMAIN_LABELS[domain] ??
    domain
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export function accessLevelLabel(level: string): string {
  return ACCESS_LEVEL_LABELS[level] ?? level;
}

export function parsePermissionString(
  permission: string,
): { domain: string; level: string } | null {
  const [domain, level] = permission.split(":");
  if (!domain || !level) {
    return null;
  }
  return { domain, level };
}

export function formatPermission(permission: string): string {
  const parsed = parsePermissionString(permission);
  if (!parsed) {
    return permission;
  }
  return `${domainLabel(parsed.domain)} (${accessLevelLabel(parsed.level)})`;
}

export interface PermissionDomainGroup {
  domain: string;
  label: string;
  levels: string[];
}

// Flattens the catalog into a stable, alphabetically-sorted list of domain
// groups for rendering grouped permission checkboxes.
export function groupPermissions(
  catalog: PermissionCatalog,
): PermissionDomainGroup[] {
  return Object.entries(catalog)
    .map(([domain, levels]) => ({
      domain,
      label: domainLabel(domain),
      levels: [...levels].sort(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
